import { DateTime, Duration } from "luxon";

import { formatLocalDate } from "@/utils/dateFormat";

const LATE_RECORDING_THRESHOLD = Duration.fromObject({ minutes: 10 });

/**
 * Formats a message instant time-first: `4:31 PM today` when the instant falls on the same day as
 * `now`, `4:31 PM on Aug 11, 2026` otherwise.
 */
export function formatMessageTime(date: Date, now: Date = new Date()): string {
  const sameDay = DateTime.fromJSDate(date).hasSame(DateTime.fromJSDate(now), "day");
  return formatLocalDate(date, sameDay ? "h:mm a 'today'" : "h:mm a 'on' MMM d, yyyy");
}

/**
 * Returns the skew-corrected time an offline-recorded message was actually recorded, or null when
 * the message should render as a normal (live) message.
 *
 * Offline mode queues driver recordings on-device and publishes them on reconnect. When the
 * recording-to-send offset (`clientSentAt` − `clientRecordedAt`) exceeds 10 minutes, the recording
 * time is worth surfacing so dispatchers don't read a stale message as current. The returned
 * instant is `serverReceivedAt` minus that offset, which cancels out device clock skew.
 *
 * @returns the corrected recording time, or null when any timestamp is missing/unparsable or the
 * offset is within the threshold.
 */
export function lateRecordedAt({
  clientRecordedAt,
  clientSentAt,
  serverReceivedAt,
}: {
  clientRecordedAt?: string | null;
  clientSentAt?: string | null;
  serverReceivedAt?: string | null;
}): Date | null {
  if (!clientRecordedAt || !clientSentAt || !serverReceivedAt) return null;

  const recorded = DateTime.fromJSDate(new Date(clientRecordedAt));
  const sent = DateTime.fromJSDate(new Date(clientSentAt));
  const received = DateTime.fromJSDate(new Date(serverReceivedAt));
  if (!recorded.isValid || !sent.isValid || !received.isValid) return null;

  const offset = sent.diff(recorded);
  if (offset.toMillis() <= LATE_RECORDING_THRESHOLD.toMillis()) return null;

  return received.minus(offset).toJSDate();
}
